import { ConfigProvider, Empty, Table } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd/es/table';
import { Key } from 'react';
import styled from 'styled-components';
import { classic } from '~/themes';
import HeaderTable from './HeaderTable';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useLingui } from '@lingui/react';

type TablePaginationPosition =
  | 'topLeft'
  | 'topCenter'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomCenter'
  | 'bottomRight';

type IProps = {
  data: any[];
  loading: boolean;
  total: number;
  columns: any[];
  xScroll?: string;
  yScroll: boolean;
  showHeader: boolean;
  headerTitle: string;
  showFooter: boolean;
  footerTitle?: string;
  selectionType: 'checkbox' | 'radio';
  top: TablePaginationPosition | 'none';
  bottom: TablePaginationPosition;
  addButton?: boolean;
  deleteButton?: boolean;
  editButton?: boolean;
  importButton?: boolean;
  exportButton?: boolean;
  rowKey?: string;
  search?: boolean;
  page: number;
  pageSize: number;
  setSortedInfo: (sortedInfo: SorterResult<any> | SorterResult<any>[]) => void;
  setFilterInfo: (filterInfo: Record<string, FilterValue | null>) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setOpenAdd?: (openAdd: boolean) => void;
  setOpenEdit?: (openEdit: boolean) => void;
  setOpenImport?: (openImport: boolean) => void;
  setOpenDelete?: (openDelete: boolean) => void;
  setSearchText?: (searchText: string) => void;
  exportCsv?: () => void;
  setRowSelect?: (rowSelect: any[]) => void;
  refresh: () => void;
};

function CommonTable({
  data,
  columns,
  xScroll,
  yScroll,
  showHeader,
  showFooter,
  footerTitle,
  selectionType,
  headerTitle,
  top,
  bottom,
  addButton,
  deleteButton,
  editButton,
  importButton,
  exportButton,
  rowKey,
  search,
  setPage,
  setPageSize,
  setOpenAdd,
  setOpenEdit,
  setOpenDelete,
  setOpenImport,
  exportCsv,
  setRowSelect,
  setSearchText,
  loading,
  total,
  page,
  pageSize,
  setSortedInfo,
  setFilterInfo,
  refresh,
}: IProps) {
  const scroll: { x?: number | string; y?: number | string } = {};
  const { i18n } = useLingui();

  if (yScroll) {
    scroll.y = '50vh';
  }
  if (xScroll) {
    scroll.x = '100vw';
  }

  const tableColumns = columns.map((item) => ({ ...item }));

  if (xScroll === 'fixed') {
    tableColumns[0].fixed = true;
    tableColumns[tableColumns.length - 1].fixed = 'right';
  }
  const defaultFooter = () => (footerTitle ? footerTitle : 'Here is footer');

  const tableProps: TableProps<any> = {
    showHeader,
    footer: showFooter ? defaultFooter : undefined,
    scroll,
  };

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (_selectedRowKeys: Key[], selectedRows: any[]) => {
      setRowSelect && setRowSelect(selectedRows);
    },
  };

  const handleChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[]
  ) => {
    setSortedInfo(sorter);
    setFilterInfo(_filters);
  };

  return (
    <div>
      <HeaderTable
        title={headerTitle}
        addButton={addButton}
        deleteButton={deleteButton}
        editButton={editButton}
        importButton={importButton}
        exportButton={exportButton}
        search={search}
        setOpenAdd={setOpenAdd}
        setOpenEdit={setOpenEdit}
        setOpenDelete={setOpenDelete}
        setOpenImport={setOpenImport}
        setSearchText={setSearchText}
        exportCsv={exportCsv}
      />
      <ConfigProvider
        renderEmpty={() => <Empty description={i18n._('no.content')} />}
      >
        <STable
          {...tableProps}
          rowSelection={
            setRowSelect && {
              type: selectionType,
              ...rowSelection,
            }
          }
          locale={{
            filterConfirm: i18n._('common.confirm'),
            filterReset: i18n._('common.reset'),
            emptyText: i18n._('common.empty'),
            triggerDesc: i18n._('desc.sort'),
            triggerAsc: i18n._('asc.sort'),
            cancelSort: i18n._('cancel.sort'),
          }}
          rowKey={rowKey}
          columns={tableColumns}
          pagination={{
            position: [top as TablePaginationPosition, bottom],
            pageSize: pageSize,
            current: page,
            total: total,
            onChange: (_page, _pageSize) => {
              setPage(_page);
              setPageSize(_pageSize);
              refresh();
            },
          }}
          dataSource={data.map((e) => {
            return {
              ...e,
              key: e.uuid ?? e.id,
            };
          })}
          scroll={scroll}
          loading={loading}
          onChange={handleChange}
        />
      </ConfigProvider>
    </div>
  );
}

export default CommonTable;

const STable = styled(Table)`
  &&& .ant-table-title {
    background: ${classic.token?.colorPrimary};
    color: #fff;
    font-size: 24px;
    font-weight: bold;
  }
  &&& .ant-table-column-sort {
    background: none;
  }
  &&& .ant-table-column-has-sorters {
    background: #f0f0f0;
  }

  &&& th.ant-table-column-has-sorters:hover {
    background: unset;
  }
`;
